import { HitlInterrupt } from "./chat";

export const RAG_STREAM_SCHEMA_VERSION = 1;

export type RagStreamEventBase = {
  schema_version: number;
  type: string;
  request_id: string;
  run_id: string;
  seq: number;
  chat_id: string;
  chat_no: number;
};

export type RagRunStartedEvent = RagStreamEventBase & {
  type: "run.started";
  queried_model_id?: string | null;
};

export type RagRunResumedEvent = RagStreamEventBase & {
  type: "run.resumed";
  queried_model_id?: string | null;
};

export type RagMessageStartedEvent = RagStreamEventBase & {
  type: "message.started";
  message_id: string;
};

export type RagMessageDeltaEvent = RagStreamEventBase & {
  type: "message.delta";
  message_id: string;
  delta: string;
};

export type RagMessageCompletedEvent = RagStreamEventBase & {
  type: "message.completed";
  message_id: string;
};

export type RagToolStartedEvent = RagStreamEventBase & {
  type: "tool.started";
  call_id: string;
  name: string;
  arguments?: Record<string, unknown> | null;
  tool_progress?: "slow_tool" | "default";
};

export type RagToolCompletedEvent = RagStreamEventBase & {
  type: "tool.completed";
  call_id: string;
  name: string;
  ok: boolean;
  summary?: string | null;
};

export type RagRunRequiresActionEvent = RagStreamEventBase & {
  type: "run.requires_action";
  reason: string;
  action: {
    type: string;
    thread_id: string;
    prompt: string;
    tool_calls: Array<Record<string, unknown>>;
  };
};

export type RagTurnResponsePayload = {
  output: string;
  figure?: Record<string, Record<string, unknown>>;
  used_tools?: string[];
  queried_model_id?: string;
  next_model_id?: string;
  artifact_urls?: Record<string, Record<string, Record<string, string>>>;
  difficulty?: string | null;
  difficulty_source?: string | null;
  remembered?: unknown;
  turn_display?: "xcausal" | "generative";
};

export type RagAsyncJobInterrupt = {
  thread_id: string;
  job_id: string;
  tool_name: string;
  status?: "pending" | "completed" | "failed";
  trigger_chat_no?: number;
};

export type RagRunCompletedEvent = RagStreamEventBase & {
  type: "run.completed";
  response: RagTurnResponsePayload;
  tool_execution_role?: string | null;
  interrupt?: HitlInterrupt | null;
  async_job?: RagAsyncJobInterrupt | null;
};

export type RagRunErrorEvent = RagStreamEventBase & {
  type: "run.error";
  code: string;
  message: string;
};

export type RagStreamEvent =
  | RagRunStartedEvent
  | RagRunResumedEvent
  | RagMessageStartedEvent
  | RagMessageDeltaEvent
  | RagMessageCompletedEvent
  | RagToolStartedEvent
  | RagToolCompletedEvent
  | RagRunRequiresActionEvent
  | RagRunCompletedEvent
  | RagRunErrorEvent;

export function parseRagStreamPayload(raw: string): RagStreamEvent | null {
  try {
    const parsed = JSON.parse(raw) as RagStreamEvent;
    if (!parsed || typeof parsed.type !== "string") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function isRagStreamTerminal(event: RagStreamEvent): boolean {
  return (
    event.type === "run.completed" ||
    event.type === "run.requires_action" ||
    event.type === "run.error"
  );
}
